import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useMode } from '../context/ModeContext'
import { useBusinessContext } from '../context/BusinessContext'
import { useAuth } from '../context/AuthContext'
import { createBusiness } from '../api'

const THEME_KEY = 'pb-theme'

type ThemeMode = 'light' | 'dark' | 'auto'

const THEME_OPTIONS: { value: ThemeMode; icon: string; label: string }[] = [
  { value: 'light', icon: 'bi-sun-fill',       label: 'Light' },
  { value: 'dark',  icon: 'bi-moon-stars-fill', label: 'Dark'  },
  { value: 'auto',  icon: 'bi-circle-half',     label: 'Auto'  },
]

function applyTheme(mode: ThemeMode) {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
  const useDark = mode === 'dark' || (mode === 'auto' && prefersDark)
  if (useDark) {
    document.documentElement.setAttribute('data-theme', 'dark')
  } else {
    document.documentElement.removeAttribute('data-theme')
  }
}

function ThemeToggle() {
  const [mode, setMode] = useState<ThemeMode>(
    () => (localStorage.getItem(THEME_KEY) as ThemeMode | null) ?? 'auto'
  )
  const [open, setOpen] = useState(false)

  useEffect(() => {
    localStorage.setItem(THEME_KEY, mode)
    applyTheme(mode)
    if (mode !== 'auto') return
    const mq      = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => applyTheme('auto')
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [mode])

  useEffect(() => {
    if (!open) return
    const handler = () => setOpen(false)
    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [open])

  const current = THEME_OPTIONS.find(o => o.value === mode)!

  return (
    <div style={{ position: 'relative' }}>
      <button
        className="pb-theme-btn"
        title={`Theme: ${current.label}`}
        aria-label="Theme selector"
        onClick={e => { e.stopPropagation(); setOpen(o => !o) }}
      >
        <i className={`bi ${current.icon}`} />
      </button>

      {open && (
        <div
          style={{
            position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 300,
            background: 'var(--pb-card-bg, #fff)', border: '1px solid var(--pb-border)',
            borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.13)',
            minWidth: 120, overflow: 'hidden',
          }}
          onClick={e => e.stopPropagation()}
        >
          {THEME_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => { setMode(opt.value); setOpen(false) }}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                width: '100%', padding: '0.45rem 0.75rem',
                background: mode === opt.value ? 'rgba(99,102,241,0.08)' : 'none',
                border: 'none', cursor: 'pointer', fontSize: '0.83rem',
                color: mode === opt.value ? 'var(--pb-primary)' : 'var(--pb-text)',
                fontWeight: mode === opt.value ? 600 : 400,
                textAlign: 'left',
              }}
            >
              <i className={`bi ${opt.icon}`} style={{ fontSize: '0.85rem', width: 16, flexShrink: 0 }} />
              {opt.label}
              {mode === opt.value && (
                <i className="bi bi-check" style={{ marginLeft: 'auto', fontSize: '0.85rem' }} />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Business dropdown ─────────────────────────────────────────────────────────

function BusinessDropdown() {
  const { businesses, selectedBusiness, setSelectedBusiness } = useBusinessContext()
  const qc = useQueryClient()
  const [open, setOpen]       = useState(false)
  const [adding, setAdding]   = useState(false)
  const [newName, setNewName] = useState('')
  const [newContact, setNewContact] = useState('')
  const [creating, setCreating] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false); setAdding(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handleCreate = async () => {
    if (!newName.trim() || !newContact.trim()) return
    setCreating(true)
    try {
      const created = await createBusiness({ businessName: newName.trim(), contactName: newContact.trim() })
      await qc.invalidateQueries({ queryKey: ['businesses'] })
      setSelectedBusiness(created.businessID)
      setAdding(false); setNewName(''); setNewContact('')
      setOpen(false)
    } catch { /* silent — pages can surface errors */ }
    finally { setCreating(false) }
  }

  if (businesses.length === 0) return null

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.4rem',
          padding: '0.3rem 0.7rem', borderRadius: 8,
          border: '1.5px solid var(--pb-border)',
          background: 'var(--pb-card-bg, #fff)', color: 'var(--pb-text)',
          fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
          maxWidth: 180,
        }}
        title={selectedBusiness?.businessName ?? 'Select business'}
      >
        <i className="bi bi-building" style={{ flexShrink: 0, fontSize: '0.8rem' }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, maxWidth: 120 }}>
          {selectedBusiness?.businessName ?? 'Select…'}
        </span>
        <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} style={{ flexShrink: 0, fontSize: '0.65rem', opacity: 0.6 }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 400,
          background: 'var(--pb-card-bg, #fff)', border: '1px solid var(--pb-border)',
          borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.13)',
          minWidth: 200, overflow: 'hidden',
        }}>
          {businesses.map(b => (
            <button
              key={b.businessID}
              onClick={() => { setSelectedBusiness(b.businessID); setOpen(false) }}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                width: '100%', padding: '0.45rem 0.85rem',
                background: selectedBusiness?.businessID === b.businessID ? 'rgba(99,102,241,0.08)' : 'none',
                border: 'none', cursor: 'pointer', fontSize: '0.85rem', textAlign: 'left',
                color: selectedBusiness?.businessID === b.businessID ? 'var(--pb-primary)' : 'var(--pb-text)',
                fontWeight: selectedBusiness?.businessID === b.businessID ? 600 : 400,
              }}
            >
              <i className="bi bi-building" style={{ fontSize: '0.8rem', opacity: 0.6 }} />
              {b.businessName}
              {selectedBusiness?.businessID === b.businessID && (
                <i className="bi bi-check" style={{ marginLeft: 'auto', fontSize: '0.85rem' }} />
              )}
            </button>
          ))}

          <div style={{ borderTop: '1px solid var(--pb-border)', padding: '0.35rem' }}>
            {!adding ? (
              <button
                onClick={() => setAdding(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                  width: '100%', padding: '0.4rem 0.5rem', borderRadius: 7,
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: '0.82rem', color: 'var(--pb-primary)', fontWeight: 600,
                }}
              >
                <i className="bi bi-plus-lg" style={{ fontSize: '0.75rem' }} />
                Add Business
              </button>
            ) : (
              <div style={{ padding: '0.25rem 0.35rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                <input
                  autoFocus
                  placeholder="Business name *"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  style={{ width: '100%', padding: '0.3rem 0.5rem', borderRadius: 6, border: '1.5px solid var(--pb-border)', fontSize: '0.82rem', background: 'var(--pb-card-bg,#fff)', color: 'var(--pb-text)', outline: 'none' }}
                />
                <input
                  placeholder="Contact name *"
                  value={newContact}
                  onChange={e => setNewContact(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleCreate()}
                  style={{ width: '100%', padding: '0.3rem 0.5rem', borderRadius: 6, border: '1.5px solid var(--pb-border)', fontSize: '0.82rem', background: 'var(--pb-card-bg,#fff)', color: 'var(--pb-text)', outline: 'none' }}
                />
                <div style={{ display: 'flex', gap: '0.35rem' }}>
                  <button
                    onClick={handleCreate}
                    disabled={creating || !newName.trim() || !newContact.trim()}
                    style={{ flex: 1, padding: '0.3rem', borderRadius: 6, border: 'none', background: 'var(--pb-primary)', color: '#fff', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', opacity: (!newName.trim() || !newContact.trim()) ? 0.5 : 1 }}
                  >
                    {creating ? '…' : 'Create'}
                  </button>
                  <button
                    onClick={() => { setAdding(false); setNewName(''); setNewContact('') }}
                    style={{ padding: '0.3rem 0.6rem', borderRadius: 6, border: '1px solid var(--pb-border)', background: 'none', fontSize: '0.78rem', cursor: 'pointer', color: 'var(--pb-muted)' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Mode toggle ───────────────────────────────────────────────────────────────

function ModeToggle() {
  const { mode, setMode } = useMode()
  const navigate = useNavigate()

  const switchTo = (next: 'personal' | 'business') => {
    if (next === mode) return
    setMode(next)
    navigate(next === 'business' ? '/biz' : '/', { replace: true })
  }

  return (
    <div
      className="pb-mode-toggle"
      title={mode === 'personal' ? 'Switch to Business' : 'Switch to Personal'}
    >
      <button
        className={'pb-mode-btn' + (mode === 'personal' ? ' active' : '')}
        onClick={() => switchTo('personal')}
        aria-label="Personal mode"
      >
        P
      </button>
      <button
        className={'pb-mode-btn' + (mode === 'business' ? ' active' : '')}
        onClick={() => switchTo('business')}
        aria-label="Business mode"
      >
        B
      </button>
    </div>
  )
}

// ── Nav items ─────────────────────────────────────────────────────────────────

const personalNav = [
  { to: '/',           label: 'Transactions', icon: 'bi-receipt-cutoff', end: true },
  { to: '/month',      label: 'Month',        icon: 'bi-calendar3',      end: false },
  { to: '/categories', label: 'Categories',   icon: 'bi-grid',           end: false },
  { to: '/merchants',  label: 'Merchants',    icon: 'bi-shop',           end: false },
  { to: '/tags',       label: 'Tags',         icon: 'bi-tags',           end: false },
  { to: '/accounts',   label: 'Accounts',     icon: 'bi-credit-card-2-front', end: false },
]

const businessNav = [
  { to: '/biz',          label: 'Transactions', icon: 'bi-receipt-cutoff',     end: true },
  { to: '/biz/invoices', label: 'Invoices',     icon: 'bi-file-earmark-text',  end: false },
  { to: '/biz/receipts', label: 'Receipts',     icon: 'bi-file-earmark-check', end: false },
  { to: '/biz/accounts', label: 'Accounts',     icon: 'bi-credit-card-2-front', end: false },
]

// ── Layout ────────────────────────────────────────────────────────────────────

export default function Layout() {
  const { mode }    = useMode()
  const { logout }  = useAuth()
  const navigate    = useNavigate()
  const navItems    = mode === 'business' ? businessNav : personalNav

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <>
      <nav className="pb-navbar">
        <NavLink to={mode === 'business' ? '/biz' : '/'} className="pb-brand">
          <span className="pb-brand-mark">
            <i className="bi bi-layers" />
          </span>
          numerus
        </NavLink>

        <ul className="pb-nav">
          {navItems.map(item => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.end}
                className={({ isActive }) => 'pb-nav-link' + (isActive ? ' active' : '')}
              >
                <i className={'bi ' + item.icon + ' me-1'} /> {item.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {mode === 'business' && <BusinessDropdown />}
          <ModeToggle />
          <NavLink to="/settings" className="pb-theme-btn" title="Settings" aria-label="Settings">
            <i className="bi bi-gear" />
          </NavLink>
          <ThemeToggle />
          <button
            className="pb-theme-btn"
            title="Sign out"
            aria-label="Sign out"
            onClick={handleLogout}
            style={{ border: 'none', background: 'none', cursor: 'pointer' }}
          >
            <i className="bi bi-box-arrow-right" />
          </button>
        </div>
      </nav>

      <div className="container pb-main">
        <main>
          <Outlet />
        </main>
      </div>
    </>
  )
}
